import type EMAbstractLayerManager from 'src/lib/extreme_map/EMAbstractLayerManager';
import type GIMovieManager from 'src/lib/geo_item/GIMovieManager';
import type { CommentType, Lov, RoadName, RoadNameDisp } from 'src/models/master';
import type { SensorData } from 'src/models/mtx';
import type { Tag, TagItem } from 'src/models/tag';
import type { Settings as UserSettings, ShareScopeLov } from 'src/models/user';
import type { CommentFeature } from 'src/consts/comment_source_type';
import type { GeoItemMeta, GIComment, GIDebugMTX, GIKp, GIMovie, GIMovieGeoIndex, GIMovieList, GIResource } from 'src/models/geoItem';

export interface LocationRaw {
  lat: string;
  lon: string;
}

export interface Location {
  lat: number;
  lon: number;
}

export interface Group23Info {
  g2id: number;
  g2name: string;
  g2dispOrder: number | null;
  g3id: number;
  g3name: string;
  g3dispOrder: number | null;
}

export interface QueryArea {
  lat: number;
  lon: number;
  radius: number;
  dispRadius?: number;
  resultSortFunc?: (a: GIMovieList, b: GIMovieList) => number;
}

export interface CandidateFile {
  id: number;
  content: string;
  type: string;
  name: string;
  path: string;
  selected: boolean;
}

interface ValsMap<T> {
  vals: T[];
  map: Record<string, T>;
}

export interface Lovs {
  carKind: ValsMap<Lov>;
  role: ValsMap<Lov>;
  shareScope: ValsMap<ShareScopeLov>;
}

export type KpMap = Map<string, Map<string, GIKp[]>>;

export interface GeoConnection {
  srcKpEnd: number;
  dstKpStart: number;
  hasNext: boolean;
  nextKey: string;
  nested: Record<string, GeoConnection>;
}

export interface GeoConnectionResponse {
  src_kp_end: number;
  dst_kp_start: number;
  has_next: boolean;
  next_key: string;
  [key: string]: GeoConnectionResponse | number | boolean | string;
}

export type GeoConnectionsJson = Record<string, Record<string, GeoConnectionResponse>>;

export interface MasterData {
  lovs: Lovs;
  kpMap: KpMap;
  kpMapByKpUid: Map<string, GIKp>;
  commentTypeIconPaths: string[];
  commentFeaturesBySourceType: Record<string, CommentFeature[]>;
  roadNames: RoadName[];
  roadNameDisps: RoadNameDisp[];
  roadNameDispMap: Record<string, RoadNameDisp>;
  commentType: ValsMap<CommentType>;
  geoConnections: {[key: string]: {[key: string]: GeoConnection}};
  roadNameToRoadNameDispMap: Record<string, string>;
}

export interface Master extends MasterData {
  $promise: Promise<MasterData>;
}

export interface ExtremeMapEssentials {
  userSettings: UserSettings;
  kpMap: KpMap;
  geoConnections: Record<string, Record<string, GeoConnection>>;
}

// layerManager 側から呼び出される最小限のインターフェース。
// extremeMapInterface として layerManager に渡される。
export interface IExtremeMap {
  showDataLayer(metaItem: GeoItemMeta, resources: GIResource[] | GIDebugMTX, zIndexOffset?: number): void;
  removeDataLayer(layerName: string): void;
  handleLayerManagerEventClick(evtObj: MapElemInfo<GIResource>): void;
}

// ExtremeMap コンポーネントの template ref から呼び出せる expose メソッド全体。
export interface IExtremeMapRef extends IExtremeMap {
  triggerResize(): void;
  setMapHeight(h: number): void;
  refreshMovieLayer(movieLists: GIMovieList[]): void;
  fitToMovieListsExtent(movieLists: GIMovieList[]): void;
  deselectLayersExcept(exceptLayer: string): void;
  showQueryArea(queryArea: QueryArea): void;
  showQueryPin(queryArea: QueryArea): void;
  getClosestMgi(point: Location, searchOpts?: { featureIdPrefix?: number }): GIMovieGeoIndex | null;
  moveCenterTo(location: Location): void;
  hideQueryArea(): void;
}

export interface IMoviePlayerControlArea {
  emitSeekChange(msec: number): void;
  resetVolume(): void;
}

export interface IMovieMetaInfoArea {
  setSensorData(sensorData: SensorData[]): void;
  setAllUIStateToDefault(): void;
}

export interface IMoviePlayerVideo {
  prepareVideojs(): void;
  getScreenshotBlob(): Promise<Blob | string | null>;
  changeVjsPlayMode(isPlayBackward: boolean): void;
}

export interface IShortcutKeyActionable {
  shortcutKeyAction(key: string): void;
}

export interface LayerInfo {
  name: string;
  dispName: string;
  hideToggleCheckbox?: boolean;
  isCreatable?: boolean;
  isTabPanel?: boolean;
  tabLabel?: string;
}

export interface MovieLayerMeta {
  name: string;
  dispName: string;
  manager: GIMovieManager | null;
  layerManager: EMAbstractLayerManager | null;
}

export const initMovieLayerMeta = (): MovieLayerMeta => ({
  name: '',
  dispName: '',
  manager: null,
  layerManager: null,
});

export interface TopMovieSearchParams {
  dtFrom: Date;
  dtTo: Date;
  roadNameDispObj?: RoadNameDisp | null;
  direction?: string;
  kpCalcFrom?: string;
  kpCalcTo?: string;
  carName?: string;
  tags?: TagItem[];
}

export type MovieSearchMode = 'none' | 'detail' | 'area' | 'route' | 'id';

export interface MovieTagModalParams {
  show: boolean;
  tags: Tag[];
  movie: null | GIMovie;
}

export interface ExtParams {
  lat?: number;
  lon?: number;
  commentId?: string;
  movieId?: number;
  startOffset?: number;
}

export interface TabResources {
  ready: boolean;
  elements: GIResource[];
}

export interface Tab {
  dataName: string;
  label: string;
  resources: TabResources;
}

export interface FetchGeoItemsParams {
  dataName: string;
  id: number | null;
}

export interface MapElemInfo<T> {
  dataName: string;
  data: T;
  // 付箋のみ
  relativeComments?: GIComment[];
}

export interface PageItem {
  pageNum: number;
}
